import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../../components/Icon.tsx';
import { getPlayUrl } from './api.ts';
import { formatElapsed } from './RecordingBar.tsx';

// Local Dhivehi strings — kept inline (not in i18n/dv.ts) since dv.ts carries
// unrelated in-flight edits on this branch and this task's commit must stay
// scoped to NowPlayingBar.tsx + YaumiyyaEditor.tsx.
const STR = {
  play: 'ޕްލޭ',
  pause: 'މެދުކަނޑާލާ',
  close: 'ބަންދުކުރޭ',
  loadError: 'އަޑު ޖެއްސުމުގައި މައްސަލައެއް ދިމާވެއްޖެ',
};

// Persistent now-playing bar, fixed to the bottom of the viewport — same
// treatment as RecordingBar (portal to document.body so editor scroll never
// moves it). Only one of {RecordingBar, NowPlayingBar} is ever mounted by
// the editor at a time (coordinated via YaumiyyaEditor's activeBar state).
export function NowPlayingBar({ recordingId, onClose }: { recordingId: string; onClose: () => void }): JSX.Element {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(false);
  const [src, setSrc] = useState<string | null>(null);

  // Fetch signed play-URL when recordingId changes. Cancel/ignore if the
  // component unmounts or recordingId changes again before the request resolves.
  useEffect(() => {
    let cancelled = false;
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setError(false);
    setSrc(null);

    getPlayUrl(recordingId)
      .then((url) => {
        if (!cancelled) setSrc(url);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [recordingId]);

  // Auto-start playback once the signed URL is loaded — the click that opened
  // this bar is the user gesture, so autoplay is permitted. If a browser still
  // blocks it, the play button remains for a manual start.
  useEffect(() => {
    if (!src || !audioRef.current) return;
    void audioRef.current.play().catch(() => {});
  }, [src]);

  function togglePlay(): void {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) void el.play().catch(() => setError(true));
    else el.pause();
  }

  function seek(e: React.ChangeEvent<HTMLInputElement>): void {
    const el = audioRef.current;
    if (!el) return;
    const next = Number(e.target.value);
    el.currentTime = next;
    setCurrentTime(next);
  }

  return createPortal(
    <div
      dir="rtl"
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-[hsl(var(--border))] bg-[hsl(var(--chrome))] shadow-[0_-2px_12px_rgba(0,0,0,0.08)]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <audio
        ref={audioRef}
        src={src ?? undefined}
        preload="metadata"
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onError={() => setError(true)}
      />
      <div className="page flex flex-wrap items-center gap-3 py-2.5">
        {error ? (
          <span className="min-w-0 flex-1 truncate text-sm text-destructive">{STR.loadError}</span>
        ) : !src ? (
          <span className="min-w-0 flex-1 truncate text-sm text-muted">ތިރިކުރަނީ...</span>
        ) : (
          <>
            <button
              type="button"
              className="grid size-11 shrink-0 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]"
              onClick={togglePlay}
              aria-label={playing ? STR.pause : STR.play}
            >
              <Icon name={playing ? 'pause' : 'play_arrow'} size={20} fill />
            </button>
            <span dir="ltr" className="mono-meta shrink-0 text-sm tabular-nums">{formatElapsed(currentTime * 1000)}</span>
            <input
              type="range"
              dir="ltr"
              className="min-w-0 flex-1"
              min={0}
              max={duration || 0}
              step={0.1}
              value={Math.min(currentTime, duration || 0)}
              onChange={seek}
              aria-label={STR.play}
            />
            <span dir="ltr" className="mono-meta shrink-0 text-sm tabular-nums">{formatElapsed(duration * 1000)}</span>
          </>
        )}
        <button type="button" aria-label={STR.close} className="grid size-11 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-muted/70" onClick={onClose}>
          <Icon name="close" size={20} />
        </button>
      </div>
    </div>,
    document.body,
  );
}
